// batch-convert Web Component - Batch document conversion
import template from './batch-convert.html';
import styles from './batch-convert.less';
import { apiClient } from '../services/api-client';
import { FileReaderUtil } from '../services/file-reader';
import { SUPPORTED_CONVERSIONS, FRAME_RATES } from '../types/api-types';
export class BatchConvert extends HTMLElement {
    constructor() {
        super(...arguments);
        this.state = {
            files: [],
            sourceFormat: null,
            targetFormat: null,
            isConverting: false
        };
        this.elements = {};
    }
    connectedCallback() {
        this.render();
        this.cacheElements();
        this.initializeEventListeners();
        this.populateFrameRates();
    }
    render() {
        const styleEl = document.createElement('style');
        styleEl.textContent = styles;
        this.appendChild(styleEl);
        const container = document.createElement('div');
        container.innerHTML = template;
        this.appendChild(container);
    }
    cacheElements() {
        this.elements.uploadArea = this.querySelector('#uploadArea');
        this.elements.fileInput = this.querySelector('#fileInput');
        this.elements.optionsPanel = this.querySelector('#optionsPanel');
        this.elements.fileCount = this.querySelector('#fileCount');
        this.elements.sourceFormat = this.querySelector('#sourceFormat');
        this.elements.targetFormat = this.querySelector('#targetFormat');
        this.elements.frameRate = this.querySelector('#frameRate');
        this.elements.frameRateGroup = this.querySelector('#frameRateGroup');
        this.elements.mode = this.querySelector('#mode');
        this.elements.modeGroup = this.querySelector('#modeGroup');
        this.elements.ignoreFrames = this.querySelector('#ignoreFrames');
        this.elements.convertBtn = this.querySelector('#convertBtn');
        this.elements.downloadBtn = this.querySelector('#downloadBtn');
        this.elements.clearBtn = this.querySelector('#clearBtn');
        this.elements.placeholderCard = this.querySelector('#placeholderCard');
        this.elements.fileList = this.querySelector('#fileList');
        this.elements.fileListBody = this.querySelector('#fileListBody');
        this.elements.progressCard = this.querySelector('#progressCard');
        this.elements.progressBar = this.querySelector('#progressBar');
        this.elements.progressText = this.querySelector('#progressText');
        this.elements.resultsCard = this.querySelector('#resultsCard');
        this.elements.resultsContent = this.querySelector('#resultsContent');
    }
    initializeEventListeners() {
        // Upload area click
        this.elements.uploadArea?.addEventListener('click', () => {
            this.elements.fileInput?.click();
        });
        // File input change
        this.elements.fileInput?.addEventListener('change', (e) => {
            const input = e.target;
            if (input.files && input.files.length > 0) {
                this.handleFileUpload(Array.from(input.files));
            }
        });
        // Drag and drop
        this.elements.uploadArea?.addEventListener('dragover', (e) => {
            e.preventDefault();
            this.elements.uploadArea?.classList.add('drag-over');
        });
        this.elements.uploadArea?.addEventListener('dragleave', () => {
            this.elements.uploadArea?.classList.remove('drag-over');
        });
        this.elements.uploadArea?.addEventListener('drop', (e) => {
            e.preventDefault();
            this.elements.uploadArea?.classList.remove('drag-over');
            if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
                this.handleFileUpload(Array.from(e.dataTransfer.files));
            }
        });
        // Source format change
        this.elements.sourceFormat?.addEventListener('change', () => {
            this.updateTargetFormats();
        });
        // Target format change
        this.elements.targetFormat?.addEventListener('change', () => {
            this.updateOptionsVisibility();
        });
        // Action buttons
        this.elements.convertBtn?.addEventListener('click', () => this.handleConvert());
        this.elements.downloadBtn?.addEventListener('click', () => this.handleDownload());
        this.elements.clearBtn?.addEventListener('click', () => this.handleClear());
    }
    populateFrameRates() {
        if (!this.elements.frameRate)
            return;
        FRAME_RATES.forEach(fr => {
            const option = document.createElement('option');
            option.value = fr.value;
            option.textContent = fr.label;
            this.elements.frameRate.appendChild(option);
        });
    }
    async handleFileUpload(files) {
        for (const file of files) {
            try {
                // Validate file size
                if (!FileReaderUtil.validateFileSize(file, 50)) {
                    alert(`File "${file.name}" is too large. Maximum size is 50MB.`);
                    continue;
                }
                // Read file content
                const content = await FileReaderUtil.readAsText(file);
                // Add to state
                const fileEntry = {
                    file,
                    content,
                    id: `file-${Date.now()}-${Math.random()}`,
                    status: 'pending'
                };
                this.state.files.push(fileEntry);
            }
            catch (error) {
                console.error('File upload error:', error);
                alert(`Failed to read file "${file.name}": ${error}`);
            }
        }
        if (this.state.files.length > 0) {
            this.showOptionsPanel();
            this.updateFileList();
        }
    }
    showOptionsPanel() {
        if (this.elements.uploadArea) {
            this.elements.uploadArea.style.display = 'none';
        }
        if (this.elements.optionsPanel) {
            this.elements.optionsPanel.style.display = 'block';
        }
        if (this.elements.placeholderCard) {
            this.elements.placeholderCard.style.display = 'none';
        }
        if (this.elements.fileList) {
            this.elements.fileList.style.display = 'block';
        }
    }
    updateFileList() {
        if (!this.elements.fileListBody)
            return;
        // Update file count
        if (this.elements.fileCount) {
            this.elements.fileCount.textContent = `${this.state.files.length} file(s)`;
        }
        // Build file list table
        let html = '';
        this.state.files.forEach(fileEntry => {
            const statusBadge = this.getStatusBadge(fileEntry.status);
            const size = FileReaderUtil.formatFileSize(fileEntry.file.size);
            html += `<tr data-file-id="${fileEntry.id}">`;
            html += `<td title="${fileEntry.file.name}">${fileEntry.file.name}</td>`;
            html += `<td>${size}</td>`;
            html += `<td>${statusBadge}</td>`;
            html += `<td>`;
            if (fileEntry.status === 'pending') {
                html += `<button class="btn btn-sm btn-outline-danger remove-file" data-file-id="${fileEntry.id}">Remove</button>`;
            }
            html += `</td>`;
            html += `</tr>`;
        });
        this.elements.fileListBody.innerHTML = html;
        // Add remove button listeners
        this.elements.fileListBody.querySelectorAll('.remove-file').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const fileId = e.target.getAttribute('data-file-id');
                this.removeFile(fileId);
            });
        });
    }
    getStatusBadge(status) {
        switch (status) {
            case 'pending':
                return '<span class="badge bg-secondary status-badge">Pending</span>';
            case 'converting':
                return '<span class="badge bg-primary status-badge">Converting...</span>';
            case 'success':
                return '<span class="badge bg-success status-badge">✓ Success</span>';
            case 'error':
                return '<span class="badge bg-danger status-badge">✗ Error</span>';
            default:
                return '';
        }
    }
    removeFile(fileId) {
        this.state.files = this.state.files.filter(f => f.id !== fileId);
        if (this.state.files.length === 0) {
            this.handleClear();
        }
        else {
            this.updateFileList();
        }
    }
    updateTargetFormats() {
        if (!this.elements.sourceFormat || !this.elements.targetFormat)
            return;
        const sourceFormat = this.elements.sourceFormat.value;
        const supportedTargets = SUPPORTED_CONVERSIONS[sourceFormat] || [];
        // Clear current options
        this.elements.targetFormat.innerHTML = '<option value="">-- Select Target Format --</option>';
        // Add supported options
        supportedTargets.forEach(format => {
            const option = document.createElement('option');
            option.value = format;
            option.textContent = format;
            this.elements.targetFormat.appendChild(option);
        });
        this.updateOptionsVisibility();
    }
    updateOptionsVisibility() {
        const targetFormat = this.elements.targetFormat?.value || '';
        const needsOptions = targetFormat === 'AvidDS';
        if (this.elements.frameRateGroup) {
            this.elements.frameRateGroup.style.display = needsOptions ? 'block' : 'none';
        }
        if (this.elements.modeGroup) {
            this.elements.modeGroup.style.display = needsOptions ? 'block' : 'none';
        }
    }
    async handleConvert() {
        const sourceFormat = this.elements.sourceFormat?.value;
        const targetFormat = this.elements.targetFormat?.value;
        if (!sourceFormat) {
            alert('Please select a source format');
            return;
        }
        if (!targetFormat) {
            alert('Please select a target format');
            return;
        }
        if (this.state.files.length === 0) {
            alert('Please upload at least one file');
            return;
        }
        this.state.isConverting = true;
        this.setButtonLoading(this.elements.convertBtn, true);
        // Show progress
        if (this.elements.progressCard) {
            this.elements.progressCard.style.display = 'block';
        }
        try {
            // Prepare batch request
            const fileContents = {};
            this.state.files.forEach(f => {
                fileContents[f.file.name] = f.content;
            });
            const options = {
                frameRate: this.elements.frameRate?.value,
                mode: this.elements.mode?.value,
                ignoreFrames: this.elements.ignoreFrames?.checked || false
            };
            // Update progress
            this.updateProgress(0, 'Starting batch conversion...');
            // Call batch convert API
            const result = await apiClient.batchConvert({
                fileContents,
                sourceFormat,
                targetFormat,
                options
            });
            // Update progress
            this.updateProgress(100, 'Conversion complete!');
            // Store result
            this.state.resultZipData = result.zipFileBase64;
            // Show results
            this.displayResults(result);
            // Show download button
            if (this.elements.downloadBtn) {
                this.elements.downloadBtn.style.display = 'block';
            }
            // Update file statuses
            this.state.files.forEach(f => {
                f.status = 'success'; // Simplified - in reality we'd check individual results
            });
            this.updateFileList();
        }
        catch (error) {
            console.error('Batch conversion error:', error);
            alert(`Batch conversion failed: ${error}`);
            this.updateProgress(0, 'Conversion failed');
            // Mark all as error
            this.state.files.forEach(f => {
                if (f.status === 'converting') {
                    f.status = 'error';
                    f.error = String(error);
                }
            });
            this.updateFileList();
        }
        finally {
            this.state.isConverting = false;
            this.setButtonLoading(this.elements.convertBtn, false);
        }
    }
    updateProgress(percentage, text) {
        if (this.elements.progressBar) {
            this.elements.progressBar.style.width = `${percentage}%`;
            this.elements.progressBar.textContent = `${percentage}%`;
        }
        if (this.elements.progressText) {
            this.elements.progressText.textContent = text;
        }
    }
    displayResults(result) {
        if (!this.elements.resultsCard || !this.elements.resultsContent)
            return;
        this.elements.resultsCard.style.display = 'block';
        const successCount = result.results?.filter((r) => r.success).length || this.state.files.length;
        const warningCount = result.results?.filter((r) => r.hasWarnings).length || 0;
        const errorCount = result.results?.filter((r) => !r.success).length || 0;
        let html = '<div class="results-summary">';
        html += '<div class="stat-card success">';
        html += `<div class="stat-value">${successCount}</div>`;
        html += '<div class="stat-label">Successful</div>';
        html += '</div>';
        if (warningCount > 0) {
            html += '<div class="stat-card warning">';
            html += `<div class="stat-value">${warningCount}</div>`;
            html += '<div class="stat-label">With Warnings</div>';
            html += '</div>';
        }
        if (errorCount > 0) {
            html += '<div class="stat-card error">';
            html += `<div class="stat-value">${errorCount}</div>`;
            html += '<div class="stat-label">Failed</div>';
            html += '</div>';
        }
        html += '</div>';
        html += '<div class="alert alert-success">';
        html += '<strong>✓ Batch conversion complete!</strong><br>';
        html += `Converted ${successCount} file(s) successfully.`;
        if (warningCount > 0) {
            html += `<br>${warningCount} file(s) have warnings (included as .warnings.txt files in the ZIP).`;
        }
        html += '</div>';
        this.elements.resultsContent.innerHTML = html;
    }
    handleDownload() {
        if (!this.state.resultZipData) {
            alert('No conversion result available');
            return;
        }
        // Decode base64 to binary
        const binaryString = atob(this.state.resultZipData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/zip' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `batch-conversion-${Date.now()}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
    handleClear() {
        // Reset state
        this.state.files = [];
        this.state.sourceFormat = null;
        this.state.targetFormat = null;
        this.state.resultZipData = undefined;
        // Reset UI
        if (this.elements.uploadArea) {
            this.elements.uploadArea.style.display = 'block';
        }
        if (this.elements.optionsPanel) {
            this.elements.optionsPanel.style.display = 'none';
        }
        if (this.elements.placeholderCard) {
            this.elements.placeholderCard.style.display = 'block';
        }
        if (this.elements.fileList) {
            this.elements.fileList.style.display = 'none';
        }
        if (this.elements.progressCard) {
            this.elements.progressCard.style.display = 'none';
        }
        if (this.elements.resultsCard) {
            this.elements.resultsCard.style.display = 'none';
        }
        if (this.elements.downloadBtn) {
            this.elements.downloadBtn.style.display = 'none';
        }
        // Reset form
        if (this.elements.sourceFormat) {
            this.elements.sourceFormat.value = '';
        }
        if (this.elements.targetFormat) {
            this.elements.targetFormat.innerHTML = '<option value="">-- Select Target Format --</option>';
        }
        if (this.elements.fileInput) {
            this.elements.fileInput.value = '';
        }
        // Reset progress
        this.updateProgress(0, 'Ready to convert');
    }
    setButtonLoading(button, loading) {
        const spinner = button.querySelector('.spinner-border');
        const text = button.querySelector('.btn-text');
        if (loading) {
            button.disabled = true;
            spinner?.classList.remove('d-none');
            if (text)
                text.textContent = 'Converting...';
        }
        else {
            button.disabled = false;
            spinner?.classList.add('d-none');
            if (text)
                text.textContent = 'Convert All';
        }
    }
}
// Register the custom element
customElements.define('batch-convert', BatchConvert);
